import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormState('success');
      setTimeout(() => setFormState('idle'), 3000);
      return;
    }

    setFormState('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      formData.delete('website_alt');

      const response = await fetch('http://dqcmwfc.com/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
        setTimeout(() => setFormState('idle'), 3000);
      } else {
        const serverMsg = (parsed?.meta as Record<string, string>)?.message
          || (parsed?.meta as Record<string, string>)?.detail
          || (parsed?.message as string)
          || responseText
          || '提交失败，请稍后再试';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(serverMsg);
        }
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormState('error');
    }
  };

  const breadcrumbs = [
    { label: '首页', path: '/' },
    { label: '关于我们', path: '/about' },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '073漫画网',
    url: 'http://dqcmwfc.com/',
    description: '在线免费阅读下拉式漫画平台，操作便捷、加载快速，追漫不受限制。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'hello@dqcmwfc.com',
      availableLanguage: ['Chinese'],
    },
  };

  const webSiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '073漫画网',
    url: 'http://dqcmwfc.com/',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'http://dqcmwfc.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '关于我们 - 073漫画网',
    description: '了解073漫画网的品牌故事、口碑评价与联系方式。',
    url: 'http://dqcmwfc.com/about',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((bc, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: { '@id': `http://dqcmwfc.com/${bc.path}`, name: bc.label },
      })),
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '073漫画网是什么平台？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '073漫画网是一个专注于提供优质漫画在线阅读的平台。我们以"操作便捷、加载快速、追漫不受限制"为核心理念，致力于为广大漫画爱好者打造最舒适的在线漫画阅读体验。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系073漫画网？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过电子邮箱 hello@dqcmwfc.com 联系我们，也可以在本页面填写联系表单，我们的团队会在24小时内回复。',
        },
      },
    ],
  };

  return (
    <>
      <Header />
      <main className="pt-16">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(webSiteSchema) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />

        <nav className="max-w-[1280px] mx-auto px-6 md:px-10 py-4" aria-label="面包屑导航">
          <ol className="flex items-center gap-2 text-xs text-foreground-500">
            {breadcrumbs.map((bc, i) => (
              <li key={bc.path} className="flex items-center gap-2">
                {i > 0 && <span className="text-foreground-300">/</span>}
                {i < breadcrumbs.length - 1 ? (
                  <Link to={bc.path} className="hover:text-primary-600 transition-colors">
                    {bc.label}
                  </Link>
                ) : (
                  <span className="text-foreground-700 font-medium">{bc.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>

        <section className="max-w-[1280px] mx-auto px-6 md:px-10 py-8 md:py-12">
          <div className="text-center mb-12">
            <h1 className="text-2xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
              关于073漫画网
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-3xl mx-auto leading-relaxed">
              073漫画网创立于2024年，是一个专注于提供优质漫画在线阅读的平台。
              我们以"操作便捷、加载快速、追漫不受限制"为核心理念，采用先进的下拉式阅读技术，
              为广大漫画爱好者打造最舒适的在线漫画阅读体验。无论你身在何处，只需打开浏览器，就能畅享精彩漫画世界。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-8 mb-16">
            <div className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70">
              <div className="w-14 h-14 md:w-16 md:h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                <i className="ri-eye-line text-2xl w-6 h-6 flex items-center justify-center"></i>
              </div>
              <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">我们的愿景</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                让每一个人都能便捷地享受漫画阅读的乐趣，打造国内最受欢迎的免费漫画阅读平台。
              </p>
            </div>
            <div className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70">
              <div className="w-14 h-14 md:w-16 md:h-16 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                <i className="ri-heart-line text-2xl w-6 h-6 flex items-center justify-center"></i>
              </div>
              <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">我们的使命</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                连接创作者与读者，让优秀的漫画作品被更多人看到，推动中国漫画文化的发展。
              </p>
            </div>
            <div className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70">
              <div className="w-14 h-14 md:w-16 md:h-16 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                <i className="ri-shield-check-line text-2xl w-6 h-6 flex items-center justify-center"></i>
              </div>
              <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">我们的承诺</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                永远免费、持续更新、尊重版权、保护用户隐私，做一个有温度的漫画平台。
              </p>
            </div>
          </div>

          <div className="mb-16" id="faq">
            <h2 className="text-xl md:text-2xl font-display font-bold text-foreground-950 mb-6 text-center">
              常见问题
            </h2>
            <div className="max-w-3xl mx-auto flex flex-col gap-3">
              {[
                {
                  q: '073漫画网是免费的吗？',
                  a: '是的！073漫画网上的所有漫画均完全免费，无需注册、无需付费。我们通过广告收入维持运营，不会向读者收取任何费用。',
                },
                {
                  q: '漫画多久更新一次？',
                  a: '我们的编辑团队会密切关注每部漫画的更新动态。热门漫画通常每周更新1-2话，我们会在第一时间同步最新章节，确保读者不会错过任何精彩内容。',
                },
                {
                  q: '支持哪些设备阅读？',
                  a: '073漫画网采用响应式设计，完美适配电脑、平板和手机等各种设备。下拉式阅读在手机端同样流畅便捷，无论您用什么设备都能获得良好的阅读体验。',
                },
                {
                  q: '如何搜索我想看的漫画？',
                  a: '您可以使用页面顶部的搜索框输入漫画名称进行搜索，也可以通过首页的分类导航按类型浏览。热门推荐和最新更新板块也能帮您发现好漫画。',
                },
              ].map((faq, idx) => (
                <details
                  key={idx}
                  className="group rounded-lg bg-background-100 border border-background-200/70 overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
                    <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                      {faq.q}
                    </span>
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform duration-200 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                  </summary>
                  <div className="px-4 md:px-5 pb-4 md:pb-5">
                    <p className="text-sm text-foreground-700 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>

          <div id="contact" className="max-w-2xl mx-auto mb-16">
            <h2 className="text-xl md:text-2xl font-display font-bold text-foreground-950 mb-2 text-center">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-8">
              有任何问题或建议？欢迎通过以下方式联系我们
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              <div className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                  <i className="ri-mail-line text-lg w-5 h-5 flex items-center justify-center"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">电子邮箱</p>
                <a href="mailto:hello@dqcmwfc.com" className="text-sm text-primary-600 hover:text-primary-700">
                  hello@dqcmwfc.com
                </a>
              </div>
              <div className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-wechat-line text-lg w-5 h-5 flex items-center justify-center"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">微信公众号</p>
                <p className="text-sm text-foreground-800">073漫画网</p>
              </div>
              <div className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                  <i className="ri-weibo-line text-lg w-5 h-5 flex items-center justify-center"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">官方微博</p>
                <p className="text-sm text-foreground-800">@073漫画网</p>
              </div>
            </div>

            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-4 bg-background-100 rounded-lg p-6 border border-background-200/70"
             
            >
              <h3 className="text-base font-heading font-semibold text-foreground-900">
                发送消息
              </h3>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="absolute left-[-9999px] opacity-0 h-0 w-0 overflow-hidden"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="contact-name" className="text-xs font-medium text-foreground-700">
                    姓名
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-200 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="contact-email" className="text-xs font-medium text-foreground-700">
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-200 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="contact-subject" className="text-xs font-medium text-foreground-700">
                  主题
                </label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  required
                  className="px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-200 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all"
                  placeholder="请输入消息主题"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="contact-message" className="text-xs font-medium text-foreground-700">
                  消息内容
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-200 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all resize-none"
                  placeholder="请输入您的消息内容（500字以内）"
                />
              </div>

              {formState === 'success' && (
                <p className="text-sm text-accent-600 font-medium">
                  消息已发送成功！我们会尽快回复您。
                </p>
              )}
              {formState === 'error' && (
                <p className="text-sm text-red-500 font-medium">
                  {formError}
                </p>
              )}

              <button
                type="submit"
                disabled={formState === 'loading'}
                className="self-start px-6 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-background-50 font-medium text-sm transition-all duration-200 whitespace-nowrap"
              >
                {formState === 'loading' ? '发送中...' : '发送消息'}
              </button>
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}